import { useState, useMemo } from 'react';
import { Box, Stack, Typography, IconButton, Button, CircularProgress, Alert, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate, useParams } from 'react-router-dom';
import {
    confirmPayrollClosure,
    downloadPayrollFile,
    regenerateEmployeeReceipt,
} from '../api/liquidacionApi';
import CardShell from '../components/CardShell';
import { HeaderPill, ColHeader } from './ClosurePayrollDataPage';
import { useClosureAccountingData } from '../hooks/useClosureAccountingData';
import { mapApiError } from '../../../../shared/utils/mapApiError';
import { fmtDMY, ymdToDate } from '../../../../shared/utils/dateFormat';
import { formatMoney } from '../utils/moneyFormat';

function formatDateRange(start, end) {
    if (!start || !end) return '';
    const parse = (v) => v instanceof Date ? v : ymdToDate(String(v).slice(0, 10));
    return `${fmtDMY(parse(start))} - ${fmtDMY(parse(end))}`;
}

const GRID_COLS = 'minmax(150px, 1.4fr) minmax(120px, 1fr) 220px 150px 40px'; 

function SummaryCard({ title, value, icon, colorSchema }) {
    return (
        <Box sx={{
            p: 1.5,
            bgcolor: 'white',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            border: '1px solid',
            borderColor: 'neutral.200',
            boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
            flex: 1
        }}>
            <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: colorSchema.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colorSchema.icon,
                flexShrink: 0 
            }}>
                {icon}
            </Box>
            <Stack sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'neutral.500', textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.2 }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: 'primary.900', lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {value}
                </Typography>
            </Stack>
        </Box>
    );
}

export default function ClosureConfirmationPage() {
    const navigate = useNavigate();
    const { closureId } = useParams();
    const { closure, employees, loading, error } = useClosureAccountingData(closureId);
    const [confirmError, setConfirmError] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);

    const totalGeneral = useMemo(() => {
        return employees.reduce((sum, emp) => sum + Number(emp.totalAmount || 0), 0);
    }, [employees]);

    if (loading || !closure || closure.id !== closureId){
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        );
    }

    const isReady = closure?.status === 'READY';

    const handleBack = () => navigate(`/rrhh/liquidacion/${closureId}/accounting`);

    const handleConfirmAndGeneratePDF = async () => {
        if (isReady) {
            navigate('/rrhh/liquidacion'); 
            return;
        }

        setConfirming(true);
        setConfirmError('');
        try {
            const response = await confirmPayrollClosure(closureId);

            const closureData = response.data || response;
            const reportFileName = closureData.accountantReportFileId;
            if (reportFileName) {
                const fileResponse = await downloadPayrollFile(reportFileName);
                const blob = new Blob([fileResponse.data || fileResponse], { type: 'application/pdf' });
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.setAttribute('download', reportFileName);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            }
            navigate('/rrhh/liquidacion');
        } catch (err) {
            setConfirmError(mapApiError(err).message || 'Error al confirmar el cierre');
            setConfirming(false);
        }
    };

    const handleDownloadReceipt = async (employee) => {
        setDownloadingReceiptId(employee.employeeId);
        setConfirmError('');
        try {
            const regenerated = await regenerateEmployeeReceipt(closureId, employee.employeeId);
            const regeneratedData = regenerated?.data || regenerated;
            const fileName = regeneratedData?.employeeReceiptFileId;

            if (!fileName) {
                throw new Error('El servidor no devolvió el recibo regenerado');
            }

            const res = await downloadPayrollFile(fileName);
            const blob = new Blob([res.data || res], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar recibo", error);
            setConfirmError(`Error al descargar el recibo de ${employee.fullName}`);
        } finally {
            setDownloadingReceiptId(null);
        }
    };

    const dateRange = closure ? formatDateRange(closure.periodStart, closure.periodEnd) : '';

    if (error) {
        return (
            <Stack spacing={2} sx={{ p: 2 }}>
                <Alert severity="error">{error}</Alert>
                <Button onClick={() => navigate('/rrhh/liquidacion')} sx={{ alignSelf: 'flex-start' }}>Volver al inicio</Button>
            </Stack>
        );
    }

    return (
        <Box sx={{ p: 2, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden' }}>

            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <IconButton  onClick={() => navigate('/rrhh/liquidacion')} disabled={confirming} sx={{ width: 40, height: 40, bgcolor: 'secondary.500', '&:hover': { bgcolor: 'secondary.600' } }}>
                    <ArrowBackIcon sx={{ color: 'logoblanco', fontSize: 32 }} />
                </IconButton>

                <HeaderPill sx={{ bgcolor: 'secondary.200' }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'primary.900' }}>
                         {isReady ? `Resumen Liquidacion: ${closure?.name || ''}` : 'Confirmación y Reportes'}
                    </Typography>
                </HeaderPill>

                {dateRange && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: 'secondary.50', borderRadius: '20px', border: '1.5px solid', borderColor: 'secondary.200' }}>
                        <CalendarTodayIcon sx={{ fontSize: 18, color: 'primary.700' }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'primary.900' }}>{dateRange}</Typography>
                    </Box>
                )}
            </Stack>

            {confirmError && <Alert severity="error" onClose={() => setConfirmError('')}>{confirmError}</Alert>}

            <Stack direction="row" spacing={2} sx={{ flexShrink: 0, width: '100%' }}>
                <SummaryCard 
                    title="Nombre del Cierre" 
                    value={closure?.name || '—'} 
                    icon={<CheckCircleOutlineIcon fontSize="small" />} 
                    colorSchema={{ bg: 'primary.50', icon: 'primary.600' }} 
                />
                <SummaryCard 
                    title="Empleados a Liquidar" 
                    value={employees.length} 
                    icon={<PeopleOutlineIcon fontSize="small" />} 
                    colorSchema={{ bg: 'secondary.100', icon: 'primary.700' }} 
                />
                <SummaryCard 
                    title="Total General a Pagar" 
                    value={formatMoney(totalGeneral)} 
                    icon={<AttachMoneyIcon fontSize="small" />} 
                    colorSchema={{ bg: 'success.50', icon: 'success.600' }} 
                />
            </Stack>
            {!isReady && (
                <Typography sx={{ fontSize: 11, color: 'primary.600', fontStyle: 'italic', flexShrink: 0 }}>
                    Verifica que los totales sean correctos. Al confirmar se generarán los PDF.
                </Typography>
            )}

            <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", bgcolor: "#A9A9B6", borderRadius: "24px", p: 2, overflow: "hidden" }}>
                <CardShell sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2 }}>

                    <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, gap: 1.5, px: 1.5, pb: 1.5, flexShrink: 0 }}>
                        <ColHeader>Empleado</ColHeader>
                        <ColHeader>DNI / CUIT</ColHeader>
                        <ColHeader>Cuenta Destino</ColHeader>
                        <ColHeader>Monto Final</ColHeader>
                        <Box />
                    </Box>

                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        <Stack spacing={0.5}>
                            {employees.map((emp) => {
                                const destAccount = emp.cvuSnapshot || emp.accountAliasSnapshot || emp.bankSnapshot || 'Efectivo';
                                const totalVal = Number(emp.totalAmount || 0);
                                return (
                                    <Box key={emp.employeeId} sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5, bgcolor: 'neutral.50', borderRadius: '16px', border: '1px solid', borderColor: 'neutral.200' }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'primary.900' }}>{emp.fullName}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'neutral.700',textAlign: 'center' }}>{emp.cuitSnapshot || emp.dni || '—'}</Typography>
                                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'neutral.700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',textAlign: 'center' }}>{destAccount}</Typography>

                                        <Box sx={{ px: 1, py: 0.5, bgcolor: totalVal >= 0 ? 'success.50' : 'error.50', borderRadius: '20px', textAlign: 'center' }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: totalVal >= 0 ? 'success.800' : 'error.800' }}>
                                                {formatMoney(totalVal)}
                                            </Typography>
                                        </Box>
                                        
                                        {isReady && (
                                            <Tooltip
                                                title={downloadingReceiptId === emp.employeeId ? "Generando recibo..." : "Descargar recibo"}
                                                arrow
                                                placement="top"
                                            >
                                                <span>
                                                    <IconButton
                                                        onClick={() => handleDownloadReceipt(emp)}
                                                        disabled={downloadingReceiptId !== null}
                                                        size="small"
                                                        sx={{ bgcolor: 'primary.50', color: 'primary.900', '&:hover': { bgcolor: 'primary.100' } }}
                                                    >
                                                        {downloadingReceiptId === emp.employeeId
                                                            ? <CircularProgress size={18} color="inherit" />
                                                            : <DownloadIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                </CardShell>
            </Box>

            <Stack direction="row" sx={{ justifyContent: "flex-end", gap: 2, flexShrink: 0 }}>
                {!isReady && (
                    <Button onClick={handleBack} disabled={confirming} sx={{ bgcolor: "#ffcdd2", color: "primary.900", px: 3, py: 1, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 16, '&:hover': { bgcolor: "#ffb3ba" } }}>
                        Atrás
                    </Button>
                )}
                <Button
                    onClick={handleConfirmAndGeneratePDF}
                    disabled={employees.length === 0 || confirming}
                    sx={{ bgcolor: "#b9f6ca", color: "primary.900", px: 4, py: 1, borderRadius: "24px", border: "2px solid", textTransform: "none", fontWeight: 800, fontSize: 16, display: 'flex', gap: 1, '&:hover': { bgcolor: "#a5f5bd" }, '&:disabled': { opacity: 0.6 } }}
                >
                    {confirming ? <CircularProgress size={20} color="inherit" /> : (
                        isReady ? "Volver a Liquidaciones" : (
                            <>
                                <PictureAsPdfIcon />
                                Confirmar y Generar PDFs
                            </>
                        )
                    )}
                </Button>
            </Stack>
        </Box>
    );
}
